import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Search, Truck } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import Modal from "../components/Modal";
import { inputCls, labelCls } from "../utils/datetime";

const EMPTY = { name: "", contact: "", phone: "", email: "", address: "", gst: "", products: "", status: "Active" };

export default function Vendors() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);
  const me = JSON.parse(localStorage.getItem("dyntra_connect_user") || "{}");
  const canEdit = ["super_admin", "owner", "admin", "clinic_manager"].includes(me.level || "");
  const canDelete = ["super_admin", "owner", "admin"].includes(me.level || "");

  const load = () => api.get("/connect/list/vendors").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/connect/vendors", form);
      toast.success("Vendor saved");
      setForm(EMPTY); setShowForm(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not save vendor"); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/connect/vendors/${editing.id}`, editForm);
      toast.success("Vendor updated");
      setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not update vendor"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/connect/vendors/${id}`); toast.success("Vendor removed"); } catch { toast.error("Could not remove vendor"); }
    load();
  };

  const filtered = items.filter((v) => {
    const q = query.trim().toLowerCase();
    return !q || [v.name, v.contact, v.phone, v.products].some((x) => String(x || "").toLowerCase().includes(q));
  });

  const VendorForm = ({ data, setData, onSubmit, submitTestid, submitLabel }) => (
    <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-3">
      <div><label className={labelCls}>Vendor Name *</label>
        <input required data-testid="vendor-name" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} className={`mt-1 ${inputCls}`} /></div>
      <div><label className={labelCls}>Contact Person</label>
        <input data-testid="vendor-contact" value={data.contact} onChange={(e) => setData({ ...data, contact: e.target.value })} className={`mt-1 ${inputCls}`} /></div>
      <div><label className={labelCls}>Phone</label>
        <input type="tel" inputMode="tel" data-testid="vendor-phone" value={data.phone} onChange={(e) => setData({ ...data, phone: e.target.value })} className={`mt-1 ${inputCls}`} /></div>
      <div><label className={labelCls}>Email</label>
        <input type="email" data-testid="vendor-email" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} className={`mt-1 ${inputCls}`} /></div>
      <div className="sm:col-span-2"><label className={labelCls}>Address</label>
        <input data-testid="vendor-address" value={data.address} onChange={(e) => setData({ ...data, address: e.target.value })} className={`mt-1 ${inputCls}`} /></div>
      <div><label className={labelCls}>GST / Tax Details</label>
        <input data-testid="vendor-gst" value={data.gst} onChange={(e) => setData({ ...data, gst: e.target.value })} className={`mt-1 ${inputCls}`} /></div>
      <div><label className={labelCls}>Status</label>
        <select data-testid="vendor-status" value={data.status} onChange={(e) => setData({ ...data, status: e.target.value })} className={`mt-1 ${inputCls}`}>
          {["Active", "Inactive"].map((s) => <option key={s}>{s}</option>)}
        </select></div>
      <div className="sm:col-span-2"><label className={labelCls}>Products Supplied</label>
        <input data-testid="vendor-products" placeholder="e.g. Gloves, composites, syringes" value={data.products} onChange={(e) => setData({ ...data, products: e.target.value })} className={`mt-1 ${inputCls}`} /></div>
      <div><button type="submit" data-testid={submitTestid} className="h-11 w-full rounded-full bg-pine text-ivory text-xs font-bold hover:bg-pine-deep transition-colors">{submitLabel}</button></div>
    </form>
  );

  return (
    <div data-testid="connect-vendors" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold text-pine-deep">Vendors</h2>
          <p className="text-sm text-slate-500">Suppliers linked to procurement and inventory stock.</p>
        </div>
        {canEdit && (
          <button data-testid="vendor-add-toggle" onClick={() => setShowForm(!showForm)}
            className="inline-flex items-center gap-2 rounded-full bg-teal-brand px-5 py-2.5 text-xs font-bold text-white hover:bg-pine transition-all">
            <Plus className="h-4 w-4" /> Add Vendor
          </button>
        )}
      </div>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input data-testid="vendor-search" value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Search vendors — name, contact, products…"
          className="h-11 w-full rounded-xl border border-pine/15 bg-white pl-11 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-pine" />
      </div>

      {showForm && canEdit && (
        <div className="rounded-2xl bg-white border border-pine/8 shadow-soft p-5">
          <VendorForm data={form} setData={setForm} onSubmit={add} submitTestid="vendor-submit" submitLabel="Save Vendor" />
        </div>
      )}

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((v) => (
          <div key={v.id} data-testid={`vendor-card-${v.id.slice(0, 8)}`} className="rounded-2xl bg-white border border-pine/8 shadow-soft p-5">
            <div className="flex items-start justify-between gap-2">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-teal-soft text-teal-brand"><Truck className="h-5 w-5" /></span>
              <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${v.status === "Inactive" ? "bg-red-50 text-red-500" : "bg-teal-soft text-teal-brand"}`}>{v.status || "Active"}</span>
            </div>
            <p className="mt-3 font-bold text-pine-deep">{v.name}</p>
            <p className="text-xs text-slate-500">{v.contact || "—"} {v.phone ? `· ${v.phone}` : ""}</p>
            {v.products && <p className="mt-1.5 text-[11px] text-slate-400">Supplies: {v.products}</p>}
            {v.gst && <p className="text-[11px] text-slate-400">GST: {v.gst}</p>}
            {canEdit && (
              <div className="mt-3 flex gap-2">
                <button data-testid={`vendor-edit-${v.id.slice(0, 8)}`} onClick={() => { setEditing(v); setEditForm({ ...EMPTY, ...v }); }}
                  className="inline-flex items-center gap-1.5 text-[11px] font-bold text-teal-brand hover:text-pine"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                {canDelete && (
                  <button data-testid={`vendor-delete-${v.id.slice(0, 8)}`} onClick={() => remove(v.id)}
                    className="inline-flex items-center gap-1.5 text-[11px] font-bold text-red-500 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /> Remove</button>
                )}
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="sm:col-span-2 xl:col-span-3 rounded-2xl bg-white border border-pine/8 p-10 text-center text-slate-400 text-sm">
            <Truck className="h-8 w-8 mx-auto mb-2 text-slate-300" /> {query ? "No vendors match your search." : "No vendors yet — add your first supplier above."}
          </div>
        )}
      </div>

      {editing && (
        <Modal testid="vendor-edit-modal" wide onClose={() => setEditing(null)} title={editing.name} subtitle="Edit vendor details">
          <VendorForm data={editForm} setData={setEditForm} onSubmit={saveEdit} submitTestid="vendor-edit-save" submitLabel="Save Changes" />
        </Modal>
      )}
    </div>
  );
}
